import {
  onCleanup,
  createEffect,
  createMemo,
  createRoot,
  createSignal,
} from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/+esm';
import { insert } from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/web/+esm';
import { register, compose } from 'https://cdn.jsdelivr.net/npm/component-register@0.6.7/+esm';

import KeenSlider from 'https://cdn.jsdelivr.net/npm/keen-slider@6.8.6/+esm';

// Utility custom element functions

function createProps(raw) {
  const keys = Object.keys(raw);
  const props = {};
  for (let i = 0; i < keys.length; i++) {
    const [get, set] = createSignal(raw[keys[i]]);
    Object.defineProperty(props, keys[i], {
      get() {
        return get();
      },
      set(v) {
        set(() => v);
      },
    });
  }
  return props;
}

function lookupContext(el) {
  if (el.assignedSlot && el.assignedSlot._$owner) return el.assignedSlot._$owner;
  let next = el.parentNode;
  while (next && !next._$owner && !(next.assignedSlot && next.assignedSlot._$owner)) next = next.parentNode;
  return next && next.assignedSlot ? next.assignedSlot._$owner : el._$owner;
}

function withSolid(ComponentType) {
  return (rawProps, options) => {
    const { element } = options;
    return createRoot((dispose) => {
      const props = createProps(rawProps);
      element.addPropertyChangedCallback((key, val) => (props[key] = val));
      element.addReleaseCallback(() => {
        element.renderRoot.textContent = '';
        dispose();
      });
      const comp = ComponentType(props, options);
      return insert(element.renderRoot, comp);
    }, lookupContext(element));
  };
}

function withNoShadowDOM(ComponentType = (props, options) => {}) {
  return (props, options) => {
    const { element } = options;
    element.renderRoot = element;
    return ComponentType(props, options);
  };
}

const customShadowlessElement = (tagName, props = {}, Component = (props, options) => {}, ...rest) =>
  compose(register(tagName, props), withNoShadowDOM, withSolid, ...rest)(Component);

function initializeSliderClasses(element) {
  element.classList.add('keen-slider');
  Array.from(element.children).forEach((child) => child.classList.add('keen-slider__slide'));
}

const initializeBpConfig = (breakpoints = {}, defaultConfig) => {
  return Object.entries(breakpoints).reduce((acc, [key, value]) => {
    acc[key] = { ...value, ...defaultConfig };
    return acc;
  }, {});
};

function AutoplaySlider(props, { element }) {
  const animation = { duration: props.duration, easing: (t) => t };
  const [slider, setSlider] = createSignal(null);

  const defaultConfig = {
    loop: true,
    renderMode: 'performance',
    drag: false,
  };

  const breakpoints = createMemo(() => initializeBpConfig(props.breakpoints, defaultConfig));

  createEffect(() => {
    initializeSliderClasses(element);

    const slider = new KeenSlider(element, {
      breakpoints: breakpoints(),
      ...defaultConfig,
      created(s) {
        s.moveToIdx(1, true, animation);
      },
      updated(s) {
        s.moveToIdx(s.track.details.abs + 1, true, animation);
      },
      animationEnded(s) {
        s.moveToIdx(s.track.details.abs + 1, true, animation);
      },
    });

    setSlider(slider);

    onCleanup(() => {
      slider.destroy();
      setSlider(null);
    });
  });
}

customShadowlessElement('autoplay-slider', { breakpoints: {}, duration: 5000 }, AutoplaySlider);
